const INVITE_ROOM_PATH = /^\/room\/([A-Za-z0-9]{6})\/?$/;
const HASH_ROOM_PATH = /^\/room\/([A-Za-z0-9]{6})\/?$/;
const ROOM_CODE_SEGMENT = /^[A-Za-z0-9]{6}$/;

/**
 * Returns normalized 6-char room code from paths like `/room/abc123` or `/room/abc123/`.
 */
export function parseInviteRoomCodeFromPath(pathname: string): string | null {
  const m = pathname.match(INVITE_ROOM_PATH);
  return m ? m[1].toUpperCase() : null;
}

/**
 * Reads invite room code from pathname `/room/…`, hash `#/room/…`, or `?room=` / `?invite=`.
 * Hash form works when the host does not rewrite `/room/*` to the SPA shell.
 */
export function parseInviteRoomCodeFromLocation(): string | null {
  if (typeof window === 'undefined') return null;

  const fromPath = parseInviteRoomCodeFromPath(window.location.pathname);
  if (fromPath) return fromPath;

  const hashRaw = window.location.hash.replace(/^#/, '');
  const hashMatch =
    hashRaw.match(HASH_ROOM_PATH) || hashRaw.match(/^room\/([A-Za-z0-9]{6})\/?$/);
  if (hashMatch) return hashMatch[1].toUpperCase();

  const params = new URLSearchParams(window.location.search);
  for (const key of ['room', 'invite'] as const) {
    const v = params.get(key);
    if (v && ROOM_CODE_SEGMENT.test(v)) return v.toUpperCase();
  }

  return null;
}

/** Invite URL uses hash so static hosts still load `/` + SPA without `/room/*` server rewrites. */
export function publicInviteUrlForRoomCode(roomCode: string): string {
  const lower = roomCode.trim().toUpperCase().toLowerCase();
  // Avoid non-www -> www redirect that can drop hash fragments in some WebViews.
  if (typeof window !== 'undefined' && window.location?.origin) {
    const origin = new URL(window.location.origin);
    if (origin.hostname === 'msgames26.com') {
      origin.hostname = 'www.msgames26.com';
    }
    return `${origin.origin}/#/room/${lower}`;
  }
  return `https://www.msgames26.com/#/room/${lower}`;
}

/** Strip invite from path, hash, or query after join/dismiss. */
export function clearInviteFromAddressBar(): void {
  if (typeof window === 'undefined') return;
  if (parseInviteRoomCodeFromLocation() === null) return;

  const url = new URL(window.location.href);
  url.pathname = '/';
  url.hash = '';
  url.searchParams.delete('room');
  url.searchParams.delete('invite');
  const q = url.searchParams.toString();
  window.history.replaceState({}, '', q ? `/?${q}` : '/');
}

/** After joining or rejoining, normalize the address bar when landing from an invite URL. */
export function replaceInviteUrlWithHomeIfMatchingRoom(roomCode: string): void {
  const invite = parseInviteRoomCodeFromLocation();
  if (invite && invite === roomCode.trim().toUpperCase()) {
    clearInviteFromAddressBar();
  }
}
